import { useEffect } from 'react';
import { Send, Check, AlertCircle, X } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Spinner from '../ui/Spinner';
import { useNewsletterSubscribe } from '../../hooks/useNewsletterSubscribe';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgb(0 0 0 / 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 28rem;
  background: ${(p) => p.theme.colors.card};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius['2xl']};
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 24px 48px rgb(0 0 0 / 0.2);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${(p) => p.theme.colors.mutedForeground};
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.colors.muted};
    color: ${(p) => p.theme.colors.foreground};
  }
`;

const Title = styled.h2`
  font-family: ${(p) => p.theme.typography.fonts.display};
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 2rem 0.5rem 0;
`;

const Description = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.9375rem;
  color: ${(p) => p.theme.colors.mutedForeground};
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const EmailInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 2.75rem;
  padding: 0 1rem;
  border-radius: ${(p) => p.theme.borderRadius.lg};
  border: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.foreground};
  font-size: 1rem;

  &:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.colors.primary}22;
  }

  &:disabled {
    opacity: 0.65;
  }
`;

const SubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.75rem;
  border: none;
  border-radius: ${(p) => p.theme.borderRadius.lg};
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.primaryForeground};
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Feedback = styled.div`
  margin-top: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  ${(p) =>
    p.$type === 'success' &&
    `
    background: ${p.theme.colors.success}22;
    color: ${p.theme.colors.success};
    border: 1px solid ${p.theme.colors.success}55;
  `}

  ${(p) =>
    p.$type === 'error' &&
    `
    background: ${p.theme.colors.error}22;
    color: ${p.theme.colors.error};
    border: 1px solid ${p.theme.colors.error}55;
  `}

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

export default function SubscribeModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { email, setEmail, isSubmitting, message, messageType, submit, reset } =
    useNewsletterSubscribe();

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submit();
    if (result?.success) {
      setTimeout(() => onClose(), 2500);
    }
  };

  return (
    <Overlay onClick={onClose} role="presentation">
      <Dialog
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscribe-modal-title"
      >
        <CloseBtn type="button" onClick={onClose} aria-label={t('common.close')}>
          <X size={18} />
        </CloseBtn>
        <Title id="subscribe-modal-title">{t('newsletter.heading')}</Title>
        <Description>{t('newsletter.description')}</Description>
        <Form onSubmit={handleSubmit}>
          <EmailInput
            type="email"
            autoFocus
            placeholder={t('newsletter.placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            required
            aria-label={t('newsletter.placeholder')}
          />
          <SubmitBtn type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" />
                {t('common.loading')}
              </>
            ) : (
              <>
                {t('newsletter.button')}
                <Send size={18} />
              </>
            )}
          </SubmitBtn>
        </Form>
        {message ? (
          <Feedback $type={messageType}>
            {messageType === 'success' ? <Check /> : <AlertCircle />}
            {message}
          </Feedback>
        ) : null}
      </Dialog>
    </Overlay>
  );
}
